import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { vippsState } from "@/lib/vippsConstants";
import { formatKr, formatDateTime, copyText } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Copy, ExternalLink, RefreshCw, Loader2, Smartphone, Settings2 } from "lucide-react";

/**
 * Vipps-kort i fakturadetaljer (eier): betalingslenke, kopier/åpne, betalingsforsøk og manuell statussjekk.
 * `onChanged` kalles når en synk kan ha endret fakturaen (f.eks. markert betalt).
 */
export const VippsPaymentCard = ({ invoiceId, invoiceStatus, isCredit, onChanged }) => {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/sales/payments/vipps/invoices/${invoiceId}`);
      setInfo(r.data);
      setError("");
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  useEffect(() => { setInfo(null); load(); }, [invoiceId, invoiceStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isCredit || invoiceStatus === "kladd") return null;
  if (error) return null;
  if (!info) return <Skeleton className="h-24" />;

  const hasPayments = (info.payments || []).length > 0;
  // Ikke aktivert og ingen forsøk: vis kun et diskret tips på åpne fakturaer
  if (!info.enabled && !hasPayments) {
    if (!["utstedt", "sendt", "delvis_betalt"].includes(invoiceStatus)) return null;
    return (
      <div className="rounded-lg border border-dashed px-3 py-2.5 text-xs text-muted-foreground flex items-start gap-2" data-testid="vipps-card-disabled">
        <Smartphone className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
        <p>La kunden betale med Vipps eller kort rett fra fakturaen. <Link to="/faktura/betaling" className="underline font-medium text-foreground" data-testid="vipps-card-setup-link">Sett opp Vipps</Link></p>
      </div>
    );
  }

  const copy = async () => { if (info.url && (await copyText(info.url))) toast.success("Betalingslenken er kopiert"); };
  const sync = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/sales/payments/vipps/invoices/${invoiceId}/sync`);
      await load();
      if (r.data.checked === 0) toast.info("Ingen åpne Vipps-betalinger å sjekke");
      else toast.success(`Status oppdatert fra Vipps (${r.data.checked} ${r.data.checked === 1 ? "betaling" : "betalinger"})`);
      onChanged?.();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const openCount = (info.payments || []).filter((p) => vippsState(p.state).open).length;

  return (
    <Card data-testid="vipps-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><Smartphone className="h-4 w-4 text-primary" /> Vipps og kort</span>
          <Link to="/faktura/betaling" className="text-muted-foreground hover:text-foreground" aria-label="Vipps-innstillinger" data-testid="vipps-card-settings-link"><Settings2 className="h-4 w-4" /></Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {info.enabled && info.payable && info.url ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Betalingslenke – ligger på PDF (knapp + QR) og i e-posten</p>
            <div className="rounded-lg border bg-secondary px-3 py-2 flex items-center gap-2">
              <code className="text-[12px] truncate flex-1" title={info.url} data-testid="vipps-card-url">{info.url.replace(/^https?:\/\//, "")}</code>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={copy} aria-label="Kopier betalingslenke" data-testid="vipps-card-copy"><Copy className="h-3.5 w-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" asChild><a href={info.url} target="_blank" rel="noreferrer" aria-label="Åpne betalingssiden" data-testid="vipps-card-open"><ExternalLink className="h-3.5 w-3.5" /></a></Button>
            </div>
          </div>
        ) : info.enabled && !info.payable ? (
          <p className="text-xs text-muted-foreground" data-testid="vipps-card-not-payable">{invoiceStatus === "betalt" ? "Fakturaen er betalt – lenken er ikke lenger aktiv." : "Fakturaen kan ikke betales via Vipps nå."}</p>
        ) : !info.enabled ? (
          <p className="text-xs text-muted-foreground" data-testid="vipps-card-off">Vipps er slått av for virksomheten. Tidligere betalingsforsøk vises under.</p>
        ) : null}

        {hasPayments ? (
          <ul className="divide-y -mx-6 border-t" data-testid="vipps-card-payments">
            {info.payments.map((p) => {
              const s = vippsState(p.state);
              const Icon = s.icon;
              return (
                <li key={p.id} className="flex items-center gap-3 px-6 py-2.5" data-testid={`vipps-payment-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium tnum">{formatKr(p.amount_ore / 100)} <span className="text-muted-foreground font-normal">· {p.method_label}</span></p>
                    <p className="text-xs text-muted-foreground truncate">{formatDateTime(p.updated_at || p.created_at)} · ref. {p.reference}</p>
                  </div>
                  <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap", s.cls)} data-testid={`vipps-payment-state-${p.id}`}><Icon className="h-3 w-3" /> {s.label}</span>
                </li>
              );
            })}
          </ul>
        ) : info.enabled && info.payable ? (
          <p className="text-xs text-muted-foreground" data-testid="vipps-card-no-payments">Kunden har ikke startet noen Vipps-betaling ennå.</p>
        ) : null}

        {openCount > 0 || (info.enabled && info.payable) ? (
          <Button variant="outline" size="sm" className="w-full" onClick={sync} disabled={busy} data-testid="vipps-card-sync">{busy ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-2" />} Sjekk status hos Vipps{openCount ? ` (${openCount} åpen${openCount === 1 ? "" : "e"})` : ""}</Button>
        ) : null}
      </CardContent>
    </Card>
  );
};
